import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false ,  // send the HttpOnly auth cookie cross-origin
});

// Response interceptor — surface 403s clearly instead of silently failing
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      // Caller can catch this and show a toast / redirect
      return Promise.reject(new Error('You do not have permission to perform this action.'));
    }
    return Promise.reject(error);
  }
);

// Auth
export const login      = (data) => api.post('/auth/login/', data);
export const logout     = ()     => api.post('/auth/logout/');
export const demoLogin  = ()     => api.post('/auth/demo-login/');
export const getMe      = ()     => api.get('/auth/me/');

// Users
export const getUsers    = ()     => api.get('/users/');
export const createUser  = (data) => api.post('/users/', data);
export const updateUser  = (id,d) => api.patch(`/users/${id}/`, d);
export const deleteUser  = (id)   => api.delete(`/users/${id}/`);

// Rooms
export const getRooms    = ()     => api.get('/rooms/');
export const createRoom  = (data) => api.post('/rooms/', data);
export const deleteRoom  = (id)   => api.delete(`/rooms/${id}/`);

// Guests
export const getGuests   = ()     => api.get('/guests/');
export const createGuest = (data) => api.post('/guests/', data);
export const checkoutGuest = (id, data={}) => api.post(`/guests/${id}/checkout/`, data);

// Menu
export const getMenu     = (type) => api.get(`/menu/?menu_type=${type}`);
export const getAllMenu   = ()     => api.get('/menu/');
export const createMenuItem = (data) => api.post('/menu/', data);
export const updateMenuItem = (id,d) => api.patch(`/menu/${id}/`, d);
export const deleteMenuItem = (id)   => api.delete(`/menu/${id}/`);

// Menu Categories
export const getMenuCategories    = (type) => api.get(`/menu-categories/${type ? `?menu_type=${type}` : ''}`);
export const createMenuCategory   = (data) => api.post('/menu-categories/', data);
export const updateMenuCategory   = (id,d) => api.patch(`/menu-categories/${id}/`, d);
export const deleteMenuCategory   = (id)   => api.delete(`/menu-categories/${id}/`);

// Orders
export const getOrders        = ()     => api.get('/orders/');
export const createOrder      = (data) => api.post('/orders/', data);
export const updateKitchenStatus = (id,s) => api.patch(`/orders/${id}/update_kitchen_status/`, {kitchen_status:s});

// Bills
export const getBills         = (params='') => api.get(`/bills/${params}`);
export const requestPayment   = (id, data) => api.post(`/bills/${id}/request_payment/`, data);
export const confirmPayment   = (id,d) => api.post(`/bills/${id}/confirm_payment/`, d);

// Payments
export const getPayments      = ()     => api.get('/payments/');

// Pool
export const getPoolEntries   = ()     => api.get('/pool/');
export const createPoolEntry  = (data) => api.post('/pool/', data);
export const getKidsAmenities  = ()     => api.get('/kids-amenities/');
export const createKidsBooking = (data) => api.post('/kids-amenities/', data);

// Reports
export const getReports       = ()     => api.get('/reports/');

export default api;

// Public (no auth required)
export const getPublicRooms      = (params='') => api.get(`/public/rooms/${params}`);

// Reservations
export const getReservations     = ()     => api.get('/reservations/');
export const createReservation   = (data) => api.post('/reservations/', data);
export const confirmReservation  = (id,d) => api.post(`/reservations/${id}/confirm/`, d);
export const cancelReservation   = (id)   => api.post(`/reservations/${id}/cancel/`);
export const checkinReservation  = (id,d) => api.post(`/reservations/${id}/checkin/`, d);

// Conference Rooms
export const getConferenceRooms  = (params='') => api.get(`/conference-rooms/${params}`);
export const createConferenceRoom = (data) => api.post('/conference-rooms/', data);
export const updateConferenceRoom = (id,d) => api.patch(`/conference-rooms/${id}/`, d);
export const deleteConferenceRoom = (id)   => api.delete(`/conference-rooms/${id}/`);

// Conference Bookings
export const getConferenceBookings    = ()     => api.get('/conference-bookings/');
export const createConferenceBooking  = (data) => api.post('/conference-bookings/', data);
export const confirmConferenceBooking = (id,d) => api.post(`/conference-bookings/${id}/confirm/`, d);
export const cancelConferenceBooking  = (id)   => api.post(`/conference-bookings/${id}/cancel/`);
export const payConferenceBooking     = (id,d) => api.post(`/conference-bookings/${id}/complete_payment/`, d);

// Gate Visitor Log
export const getGateVisitors      = (params='') => api.get(`/gate-visitors/${params}`);
export const createGateVisitor    = (data)       => api.post('/gate-visitors/', data);
export const exitGateVisitor      = (id)         => api.post(`/gate-visitors/${id}/exit/`);
export const scanExitGateVisitor  = (token)      => api.post(`/gate-visitors/scan-exit/${token}/`);
export const lookupGateQR         = (token)      => api.get(`/gate-visitors/scan-exit/${token}/`);
export const exportGateLogExcel   = (params='')  => `${BASE}/gate-visitors/export/excel/${params}`;
export const getGateFrequentVisitors = (limit=20, days=0) => api.get(`/gate-visitors/frequent/?limit=${limit}&days=${days}`);
export const exportGateLogPdf     = (params='')  => `${BASE}/gate-visitors/export/pdf/${params}`;

// Guest Folio
export const getGuestFolio       = (guestId)     => api.get(`/guests/${guestId}/folio/`);
export const getFolioCharges     = (params='')   => api.get(`/folio-charges/${params}`);
export const postFolioCharge     = (data)        => api.post('/folio-charges/', data);
export const settleFolioCharges  = (data)        => api.post('/folio-charges/settle/', data);

// Housekeeping
export const getHousekeepingLogs    = (params='') => api.get(`/housekeeping/${params}`);
export const createHousekeepingLog  = (data)      => api.post('/housekeeping/', data);
export const updateChecklist        = (id, data)  => api.post(`/housekeeping/${id}/update_checklist/`, data);
export const markCleaningDone       = (id)        => api.post(`/housekeeping/${id}/mark_done/`);
export const approveRoom            = (id)        => api.post(`/housekeeping/${id}/approve/`);
export const reportHkIssue          = (id, data)  => api.post(`/housekeeping/${id}/report_issue/`, data);
export const restoreRoom             = (data)      => api.post('/housekeeping/restore_room/', data);

// Notifications
export const getNotifications       = (params='') => api.get(`/notifications/${params}`);
export const markAllRead            = ()           => api.post('/notifications/mark_all_read/');
export const markNotifRead          = (id)         => api.post(`/notifications/${id}/mark_read/`);
export const checkNotifications     = ()           => api.get('/notifications/check/');

// Rollover
export const performRollover        = ()           => api.post('/rollover/');

// Bill print
export const markBillPrinted        = (id)        => api.post(`/bills/${id}/mark_printed/`);

// Ingredients (food costing)
export const getIngredients      = (params='') => api.get(`/ingredients/${params}`);
export const createIngredient    = (data)      => api.post('/ingredients/', data);
export const updateIngredient    = (id, data)  => api.patch(`/ingredients/${id}/`, data);
export const deleteIngredient    = (id)        => api.delete(`/ingredients/${id}/`);
export const getIngredientHistory= (id)        => api.get(`/ingredients/${id}/price_history/`);
export const getLowStockIngredients = ()       => api.get('/ingredients/low_stock/');

// Recipes
export const getRecipes          = (params='') => api.get(`/recipes/${params}`);
export const createRecipe        = (data)      => api.post('/recipes/', data);
export const updateRecipe        = (id, data)  => api.patch(`/recipes/${id}/`, data);
export const deleteRecipe        = (id)        => api.delete(`/recipes/${id}/`);
export const setRecipeIngredients= (id, data)  => api.post(`/recipes/${id}/set_ingredients/`, data);
export const scaleRecipe         = (id, data)  => api.post(`/recipes/${id}/scale/`, data);
export const publishRecipeToMenu = (id, data)  => api.post(`/recipes/${id}/publish_to_menu/`, data);
export const getChefDashboard    = ()          => api.get('/recipes/dashboard/');

// Expenses
export const getExpenses         = (params='') => api.get(`/expenses/${params}`);
export const createExpense       = (data)      => api.post('/expenses/', data);
export const updateExpense       = (id, data)  => api.patch(`/expenses/${id}/`, data);
export const deleteExpense       = (id)        => api.delete(`/expenses/${id}/`);

// Exports (direct window navigation for file download)
export const exportExcel = (params='') => `${BASE}/accountant/export/excel/${params}`;
export const exportPdf   = (params='') => `${BASE}/accountant/export/pdf/${params}`;
export const exportRecipePdf = (id)    => `${BASE}/recipes/${id}/export/pdf/`;

// Gift Shop
export const getGiftShopItems    = (params='') => api.get(`/gift-shop-items/${params}`);
export const createGiftShopItem  = (data)      => api.post('/gift-shop-items/', data);
export const updateGiftShopItem  = (id, data)  => api.patch(`/gift-shop-items/${id}/`, data);
export const deleteGiftShopItem  = (id)        => api.delete(`/gift-shop-items/${id}/`);
export const restockGiftShopItem = (id, data)  => api.post(`/gift-shop-items/${id}/restock/`, data);
export const getLowStockGiftShop = ()          => api.get('/gift-shop-items/low_stock/');

export const getGiftShopSales    = (params='') => api.get(`/gift-shop-sales/${params}`);
export const createGiftShopSale  = (data)      => api.post('/gift-shop-sales/', data);
export const markGiftSalePrinted = (id)        => api.post(`/gift-shop-sales/${id}/mark_printed/`);
export const getGiftShopSummary  = ()          => api.get('/gift-shop/summary/');
export const exportGiftShopExcel = (params='') => `${BACKEND_URL}/api/gift-shop/export/excel/${params}`;
export const exportGiftShopPdf   = (params='') => `${BACKEND_URL}/api/gift-shop/export/pdf/${params}`;

// Checkout preview
export const getCheckoutPreview  = (id)        => api.get(`/guests/${id}/checkout_preview/`);

// Gift shop cashier flow
export const confirmGiftPayment  = (id, data) => api.post(`/gift-shop-sales/${id}/confirm_payment/`, data);

// Manager bill actions
export const voidBill            = (id, data) => api.post(`/bills/${id}/void_order/`, data);
export const applyDiscount       = (id, data) => api.post(`/bills/${id}/apply_discount/`, data);
export const refundBill          = (id, data) => api.post(`/bills/${id}/refund/`, data);
export const reopenBill          = (id)       => api.post(`/bills/${id}/reopen/`);

// Audit logs
export const getAuditLogs        = (params='') => api.get(`/audit-logs/${params}`);

// Manager reports
export const getManagerReports   = (params='') => api.get(`/restaurant/reports/${params}`);

// Menu management (manager) - see menu exports above

// Returns
export const getReturns     = (params='') => api.get(`/returns/${params}`);
export const createReturn   = (data)      => api.post('/returns/', data);
export const approveReturn  = (id, data)  => api.post(`/returns/${id}/approve/`, data);
export const rejectReturn   = (id, data)  => api.post(`/returns/${id}/reject/`, data);

// Public feedback (no auth needed)
export const submitFeedback = (data) => api.post('/feedback/', data);
export const getFeedback    = (params='') => api.get(`/feedback/${params}`);

// Payment testing
export const mpesaStkPush   = (data) => api.post('/payments/mpesa-stk/', data);
export const stripePayIntent= (data) => api.post('/payments/stripe-intent/', data);

// Store / Inventory
export const getStockItems       = (params='')  => api.get(`/stock-items/${params}`);
export const createStockItem     = (data)       => api.post('/stock-items/', data);
export const updateStockItem     = (id, data)   => api.patch(`/stock-items/${id}/`, data);
export const deleteStockItem     = (id)         => api.delete(`/stock-items/${id}/`);
export const adjustStock         = (id, data)   => api.post(`/stock-items/${id}/adjust_stock/`, data);
export const getStockHistory     = (id)         => api.get(`/stock-items/${id}/history/`);
export const getStockAdjustments = (params='')  => api.get(`/stock-adjustments/${params}`);
export const getIssuances        = (params='')  => api.get(`/store-issuances/${params}`);
export const createIssuance      = (data)       => api.post('/store-issuances/', data);
export const issueVoucher        = (id, data)   => api.post(`/store-issuances/${id}/issue/`, data);
export const cancelIssuance      = (id)         => api.post(`/store-issuances/${id}/cancel/`);
export const exportIssuanceExcel = (params='')  => `${BACKEND_URL}/api/store-issuances/export/excel/${params}`;
export const exportIssuancePdf   = (params='')  => `${BACKEND_URL}/api/store-issuances/export/pdf/${params}`;

// Shopping Lists
export const getShoppingLists         = (params='') => api.get(`/shopping-lists/${params}`);
export const createShoppingList       = (data)      => api.post('/shopping-lists/', data);
export const updateShoppingList       = (id, data)  => api.patch(`/shopping-lists/${id}/`, data);
export const setShoppingListItems     = (id, data)  => api.patch(`/shopping-lists/${id}/set_items/`, data);
export const deleteShoppingList       = (id)        => api.delete(`/shopping-lists/${id}/`);
export const generateShoppingList     = ()          => api.post('/shopping-lists/generate_from_low_stock/');
export const approveShoppingList      = (id)        => api.post(`/shopping-lists/${id}/approve/`);
export const rejectShoppingList       = (id, data)  => api.post(`/shopping-lists/${id}/reject/`, data);
export const markShoppingListPurchased= (id)        => api.post(`/shopping-lists/${id}/mark_purchased/`);

// Complimentary Bills
export const getComplimentaryBills    = (params='') => api.get(`/complimentary-bills/${params}`);
export const createComplimentaryBill  = (data)      => api.post('/complimentary-bills/', data);
export const approveComplimentaryBill = (id)        => api.post(`/complimentary-bills/${id}/approve/`);
export const rejectComplimentaryBill  = (id, data)  => api.post(`/complimentary-bills/${id}/reject/`, data);

// Two-step shopping list approval
export const accountantApproveShoppingList = (id, data) => api.post(`/shopping-lists/${id}/accountant_approve/`, data);

// Purchases
export const getPurchases           = (params='') => api.get(`/purchases/${params}`);
export const createPurchase         = (data)      => api.post('/purchases/', data);
export const updatePurchase         = (id, data)  => api.patch(`/purchases/${id}/`, data);
export const deletePurchase         = (id)        => api.delete(`/purchases/${id}/`);
export const setPurchaseItems       = (id, data)  => api.post(`/purchases/${id}/set_items/`, data);
export const confirmPurchase        = (id)        => api.post(`/purchases/${id}/confirm/`);
export const cancelPurchase         = (id)        => api.post(`/purchases/${id}/cancel/`);

// Funds Dispatched
export const getFundsDispatched     = (params='') => api.get(`/funds-dispatched/${params}`);
export const createFundsDispatched  = (data)      => api.post('/funds-dispatched/', data);
export const deleteFundsDispatched  = (id)        => api.delete(`/funds-dispatched/${id}/`);

// Export: Audit Log & Movement Log
export const exportAuditLogExcel    = (params='') => `${BACKEND_URL}/api/audit-logs/export/excel/${params}`;
export const exportMovementLogExcel = (params='') => `${BACKEND_URL}/api/stock-adjustments/export/excel/${params}`;

// System Config & Branding
export const getSystemConfig    = ()        => api.get('/system-config/');
export const updateSystemConfig = (data)    => api.patch('/system-config/', data);
export const getBranding        = ()        => api.get('/branding/');
export const updateBranding     = (data)    => api.post('/branding/', data);

// Currency Rates
export const getCurrencyRates   = ()        => api.get('/currency-rates/');
export const createCurrencyRate = (data)    => api.post('/currency-rates/', data);
export const updateCurrencyRate = (id,data) => api.patch(`/currency-rates/${id}/`, data);
export const deleteCurrencyRate = (id)      => api.delete(`/currency-rates/${id}/`);

// Donations
export const getDonations       = (params='') => api.get(`/donations/${params}`);
export const createDonation     = (data)      => api.post('/donations/', data);
export const updateDonation     = (id,data)   => api.patch(`/donations/${id}/`, data);
export const deleteDonation     = (id)        => api.delete(`/donations/${id}/`);

// Leave Requests
export const getLeaveRequests   = (params='') => api.get(`/leave-requests/${params}`);
export const createLeaveRequest = (data)      => api.post('/leave-requests/', data);
export const updateLeaveRequest = (id,data)   => api.patch(`/leave-requests/${id}/`, data);
export const approveLeaveRequest= (id,data)   => api.post(`/leave-requests/${id}/approve/`, data);
export const rejectLeaveRequest = (id,data)   => api.post(`/leave-requests/${id}/reject/`, data);
export const reactivateEmployee = (id)        => api.post(`/leave-requests/${id}/reactivate/`);

// HR: Terminate employee
export const terminateEmployee  = (userId)    => api.post(`/hr/terminate/${userId}/`);

// Additional exports for stock movement PDF
export const exportMovementLogPdf = (params='') => `${BACKEND_URL}/api/stock-adjustments/export/pdf/${params}`;

// Room Status Export (Admin only)
export const exportRoomStatusExcel = (params='') => `${BACKEND_URL}/api/rooms/export/excel/${params}`;
export const exportRoomStatusPdf   = (params='') => `${BACKEND_URL}/api/rooms/export/pdf/${params}`;

// Spoilage Reports
export const getSpoilageReports     = (params='') => api.get(`/spoilage-reports/${params}`);
export const createSpoilageReport   = (data)      => api.post('/spoilage-reports/', data);
export const updateSpoilageReport   = (id,data)   => api.patch(`/spoilage-reports/${id}/`, data);
export const deleteSpoilageReport   = (id)        => api.delete(`/spoilage-reports/${id}/`);
export const setSpoilageItems       = (id,data)   => api.post(`/spoilage-reports/${id}/set_items/`, data);
export const submitSpoilageReport   = (id)        => api.post(`/spoilage-reports/${id}/submit/`);
export const approveSpoilageReport  = (id)        => api.post(`/spoilage-reports/${id}/approve/`);
export const rejectSpoilageReport   = (id,data)   => api.post(`/spoilage-reports/${id}/reject/`, data);
export const getSpoilageSummary     = ()           => api.get('/spoilage-reports/summary-stats/');
export const exportSpoilageExcel    = (params='') => `${BACKEND_URL}/api/spoilage-reports/export/excel/${params}`;
export const exportSpoilagePdf      = (params='') => `${BACKEND_URL}/api/spoilage-reports/export/pdf/${params}`;

// Assets Register
export const getAssets              = (params='') => api.get(`/assets/${params}`);
export const createAsset            = (data)      => api.post('/assets/', data);
export const updateAsset            = (id,data)   => api.patch(`/assets/${id}/`, data);
export const deleteAsset            = (id)        => api.delete(`/assets/${id}/`);
export const getAssetSummary        = ()           => api.get('/assets/register-summary/');
export const exportAssetRegisterPdf = (params='') => `${BACKEND_URL}/api/assets/register/export-pdf/${params}`;

// Hotel Reports (Accountant)
export const getAccountantReports   = (params='') => api.get(`/accountant/reports/${params}`);
export const exportAccountantExcel  = (params='') => `${BACKEND_URL}/api/accountant/export/excel/${params}`;
export const exportAccountantPdf    = (params='') => `${BACKEND_URL}/api/accountant/export/pdf/${params}`;

// Admin — Employee profiles, soft-delete, guest history
export const getEmployeeProfile     = (id)        => api.get(`/admin/users/${id}/profile/`);
export const softDeleteUser         = (id, data)  => api.post(`/admin/users/${id}/delete/`, data);
export const getGuestHistory        = ()           => api.get('/admin/guest-history/');
export const softDeleteGuest        = (id)         => api.delete(`/admin/guest-history/?id=${id}`);

// Transport Requests
export const getTransportRequests   = (params='') => api.get(`/transport-requests/${params}`);
export const createTransportRequest = (data)      => api.post('/transport-requests/', data);
export const updateTransportRequest = (id,data)   => api.patch(`/transport-requests/${id}/`, data);
export const completeTransport      = (id)        => api.post(`/transport-requests/${id}/complete/`);
export const cancelTransport        = (id)        => api.post(`/transport-requests/${id}/cancel/`);
export const deleteTransportRequest = (id)        => api.delete(`/transport-requests/${id}/`);
export const getTransportSummary    = ()           => api.get('/transport-requests/summary/');

// Attendance
export const getAttendance          = (params='') => api.get(`/attendance/${params}`);
export const createAttendance       = (data)      => api.post('/attendance/', data);
export const updateAttendance       = (id,data)   => api.patch(`/attendance/${id}/`, data);
export const clockIn                = ()           => api.post('/attendance/clock-in/');
export const clockOut               = ()           => api.post('/attendance/clock-out/');
export const getTodayAttendance     = ()           => api.get('/attendance/today-status/');
export const getMonthlyAttendance   = (month='')  => api.get(`/attendance/monthly-report/?month=${month}`);

// ── Gap Modules ───────────────────────────────────────────────────────────────

// Accounting
export const getCOA              = (params='') => api.get(`/accounts/coa/${params}`);
export const createAccount       = (data)      => api.post('/accounts/coa/', data);
export const updateAccount       = (id,data)   => api.patch(`/accounts/coa/${id}/`, data);
export const getJournals         = (params='') => api.get(`/accounts/journal/${params}`);
export const createJournal       = (data)      => api.post('/accounts/journal/', data);
export const updateJournal       = (id,data)   => api.patch(`/accounts/journal/${id}/`, data);
export const postJournal         = (id)        => api.post(`/accounts/journal/${id}/post/`);
export const reverseJournal      = (id)        => api.post(`/accounts/journal/${id}/reverse/`);
export const getTrialBalance     = (params='') => api.get(`/accounts/trial-balance/${params}`);
export const getProfitAndLoss    = (params='') => api.get(`/accounts/profit-and-loss/${params}`);
export const getBalanceSheet     = ()          => api.get('/accounts/balance-sheet/');

// Payroll (backend)
export const getPayrollEmployees = (params='') => api.get(`/payroll/employees/${params}`);
export const createPayrollEmp    = (data)      => api.post('/payroll/employees/', data);
export const updatePayrollEmp    = (id,data)   => api.patch(`/payroll/employees/${id}/`, data);
export const deletePayrollEmp    = (id)        => api.delete(`/payroll/employees/${id}/`);
export const getPayrollRuns      = ()          => api.get('/payroll/runs/');
export const processPayroll      = (data)      => api.post('/payroll/process/', data);
export const lockPayrollRun      = (id)        => api.post(`/payroll/runs/${id}/lock/`);
export const getPayslip          = (runId,empId) => api.get(`/payroll/runs/${runId}/payslip/?employee_id=${empId}`);

// Vendors / AP
export const getVendors          = (params='') => api.get(`/vendors/${params}`);
export const createVendor        = (data)      => api.post('/vendors/', data);
export const updateVendor        = (id,data)   => api.patch(`/vendors/${id}/`, data);
export const deleteVendor        = (id)        => api.delete(`/vendors/${id}/`);
export const getAPInvoices       = (params='') => api.get(`/ap/invoices/${params}`);
export const createAPInvoice     = (data)      => api.post('/ap/invoices/', data);
export const updateAPInvoice     = (id,data)   => api.patch(`/ap/invoices/${id}/`, data);
export const createAPPayment     = (data)      => api.post('/ap/payments/', data);
export const getAPAgeing         = ()          => api.get('/vendors/ap-ageing/');

// CRM
export const getCRMProfiles      = (params='') => api.get(`/crm/profiles/${params}`);
export const createCRMProfile    = (data)      => api.post('/crm/profiles/', data);
export const updateCRMProfile    = (id,data)   => api.patch(`/crm/profiles/${id}/`, data);
export const getCRMStays         = (profileId) => api.get(`/crm/stays/?profile=${profileId}`);
export const createCRMStay       = (data)      => api.post('/crm/stays/', data);
export const getComplaints       = (params='') => api.get(`/crm/complaints/${params}`);
export const createComplaint     = (data)      => api.post('/crm/complaints/', data);
export const resolveComplaint    = (id,data)   => api.post(`/crm/complaints/${id}/resolve/`, data);
export const getNPS              = (params='') => api.get(`/crm/nps/${params}`);
export const createNPS           = (data)      => api.post('/crm/nps/', data);
export const getCRMSummary       = ()          => api.get('/crm/summary/');

// Maintenance
export const getWorkOrders       = (params='') => api.get(`/maintenance/work-orders/${params}`);
export const createWorkOrder     = (data)      => api.post('/maintenance/work-orders/', data);
export const updateWorkOrder     = (id,data)   => api.patch(`/maintenance/work-orders/${id}/`, data);
export const updateWOStatus      = (id,status) => api.post(`/maintenance/work-orders/${id}/update-status/`, {status});
export const getPMSchedules      = (params='') => api.get(`/maintenance/pm/${params}`);
export const createPMSchedule    = (data)      => api.post('/maintenance/pm/', data);
export const updatePMSchedule    = (id,data)   => api.patch(`/maintenance/pm/${id}/`, data);
export const markPMDone          = (id)        => api.post(`/maintenance/pm/${id}/mark-done/`);
export const createMaintenanceCost = (data)    => api.post('/maintenance/costs/', data);
export const getMaintenanceSummary = ()        => api.get('/maintenance/summary/');

// KPI / BI
export const getKPILive          = ()          => api.get('/kpi/live/');
export const refreshKPI          = (date)      => api.post('/kpi/refresh/', { date });
export const getKPISnapshots     = (params='') => api.get(`/kpi/snapshots/${params}`);

// Chef Budget & Staff Meals
export const getChefBudgets       = (params='') => api.get(`/chef-budgets/${params}`);
export const getCurrentChefBudget = ()           => api.get('/chef-budgets/current/');
export const getChefBudgetSummary = (params='') => api.get(`/chef-budgets/summary/${params}`);
export const createChefBudget     = (data)       => api.post('/chef-budgets/', data);
export const updateChefBudget     = (id, data)   => api.patch(`/chef-budgets/${id}/`, data);
export const deleteChefBudget     = (id)         => api.delete(`/chef-budgets/${id}/`);
export const getKitchenPrepReport  = (params='') => api.get(`/kitchen/prep-report/${params}`);
export const getStaffMealLogs     = (params='') => api.get(`/staff-meal-logs/${params}`);
export const createStaffMealLog   = (data)       => api.post('/staff-meal-logs/', data);
export const updateStaffMealLog   = (id, data)   => api.patch(`/staff-meal-logs/${id}/`, data);
export const deleteStaffMealLog   = (id)         => api.delete(`/staff-meal-logs/${id}/`);
export const getStaffMealMonthly  = (params='') => api.get(`/staff-meal-logs/monthly_totals/${params}`);

// Transport Zones (admin-configurable pickup locations with per-vehicle pricing)
export const getTransportZones        = (params='') => api.get(`/transport-zones/${params}`);
export const getTransportZonesPublic  = ()           => api.get('/transport-zones/public/');
export const createTransportZone      = (data)       => api.post('/transport-zones/', data);
export const updateTransportZone      = (id, data)   => api.patch(`/transport-zones/${id}/`, data);
export const deleteTransportZone      = (id)         => api.delete(`/transport-zones/${id}/`);